<template>
  <div>
    <b-card title="Dispatch Report">
      <b-row>
        <b-col
          cols="12"
          lg="4"
          class="align-items-start"
        >
          <b-form-group label="Sorting Date">
            <b-form-datepicker
              v-model="sortingDate"
              class="w-100 sorting-date"
              @input="getSortingIdString"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          lg="4"
          class="align-items-start"
        >
          <b-form-group label="Selected Bucket">
            <v-select
              v-model="bucket"
              :reduce="option => option.value"
              label="name"
              placeholder="Select Bucket"
              :options="availableBuckets"
              @input="getSortingIdString"
            >
              <template v-slot:option="option">
                {{ option.name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          md="3"
          class="mt-md-2"
        >
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-primary"
            @click="getSessionStats"
          >
            <feather-icon
              icon="CornerLeftDownIcon"
              class="mr-50"
            />
            <span class="align-middle">Get Data</span>
          </b-button>
        </b-col>
      </b-row>
      <!-- Table and Export Buttons -->
      <div
        v-if="dispatchReport.length"
        class="d-flex flex-wrap align-items-center justify-content-between mt-2"
      >
        <b-form-group
          v-if="branchOptions.length"
          label="Filter by Branch"
          class="mb-0"
          style="min-width: 260px; max-width: 340px; flex: 1 1 260px;"
        >
          <v-select
            v-model="selectedBranch"
            :options="branchOptions"
            label="branch"
            placeholder="Select Branch"
            :clearable="true"
            class="w-100"
          />
        </b-form-group>
        <div class="d-flex mt-2">
          <b-button
            variant="outline-danger"
            class="ml-2"
            @click="exportPDF"
          >
            <feather-icon
              icon="FileTextIcon"
              class="mr-50"
            />
            Export PDF
          </b-button>
          <b-button
            variant="outline-primary"
            class="ml-2"
            @click="printTable"
          >
            <feather-icon
              icon="PrinterIcon"
              class="mr-50"
            />
            Print
          </b-button>
        </div>
      </div>
      <!-- Dispatch Report Table -->
      <b-table
        v-if="filteredDispatchReport.length"
        ref="dispatchTable"
        :items="filteredDispatchReport"
        :fields="dispatchReportFields"
        striped
        hover
        responsive
        show-empty
        class="mt-3"
      >
        <template #empty>
          <div class="text-center py-4 text-muted">
            No dispatch records found.
          </div>
        </template>
        <template #cell(branch)="data">
          <span class="font-weight-bold">{{ data.value }}</span>
        </template>
        <template #cell(bag_id)="data">
          <span>{{ data.value }}</span>
        </template>
        <template #cell(no_of_pieces)="data">
          <b-badge :variant="data.value > 0 ? 'light-success' : 'light-secondary'">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(user)="data">
          <span>{{ data.value }}</span>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BCard, BFormGroup, BFormDatepicker, BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import SuccessBell from '@core/mixins/SuccessBell'
import ErrorBell from '@core/mixins/ErrorBell'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

const sortingRepository = RepositoryFactory.get('sorting')

export default {
  directives: {
    Ripple,
  },
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BFormGroup,
    BFormDatepicker,
    BTable,
  },
  mixins: [SuccessMessage, ErrorMessage, SuccessBell, ErrorBell],
  data: () => ({
    loading: false,
    loggedIn: false,
    upcoming: 0,
    toSort: 0,
    sorted: 0,
    availableBuckets: [],
    sortingDate: new Date().toISOString().split('T')[0],
    sortingId: '',
    category: 'Night Operations',
    bucket: null,
    currentLocation: '',
    fullPath: [],
    buckets: [],
    bucketName: '',
    scanStatus: '',
    scanMsg: '',
    scanCode: '',
    scanBucket: '',
    scanWaybill: '',
    isStarting: false,
    hideWidgets: true,
    bucketColor: 'rgb(115, 103, 240)',
    sortingIdString: '',
    sessions: [],
    requiredRate: 0,
    estimatedEndTime: '',
    dispatchReport: [],
    branchOptions: [],
    selectedBranch: null,
    filteredDispatchReport: [],
    dispatchReportFields: [
      { key: 'branch', label: 'Branch' },
      { key: 'bag_id', label: 'Bag ID' },
      { key: 'no_of_pieces', label: 'No. of Pieces' },
      { key: 'user', label: 'User' },
    ],
    dispatchJsonFields: {
      Branch: { field: 'branch', callback: value => `"${value}"` },
      'Bag ID': { field: 'bag_id', callback: value => `"${value}"` },
      'No. of Pieces': { field: 'no_of_pieces', callback: value => `"${value}"` },
      User: { field: 'user', callback: value => `"${value}"` },
    },
  }),
  watch: {
    dispatchReport: {
      handler() {
        this.filterDispatchReport()
      },
      deep: true,
    },
    selectedBranch() {
      this.filterDispatchReport()
    },
  },
  mounted() {
    this.fetchBuckets()
  },
  methods: {
    getSortingIdString() {
      const selected = this.availableBuckets.find(b => b.value === this.bucket)
      this.bucketName = selected ? selected.name : ''
    },
    async getSessionStats() {
      if (!this.bucket) {
        this.showErrorMessage('Please select a bucket')
        return
      }
      try {
        const dispatchRes = await sortingRepository.getDispatchReport({
          date: this.sortingDate,
          bucket_id: Number(this.bucket),
        })
        this.dispatchReport = dispatchRes.data.data || []
        if (this.dispatchReport.length === 0) {
          this.showErrorMessage('No dispatch records found for the selected date and bucket.')
        } else {
          this.branchOptions = [...new Set(this.dispatchReport.map(item => item.branch))]
        }
      } catch (error) {
        // Handle error
      }
    },
    async fetchBuckets() {
      try {
        const response = await sortingRepository.getAvailableBucketsforReporting()
        this.availableBuckets = (response.data.data ?? []).map(bucket => ({
          value: bucket.id.toString(),
          text: `${bucket.name} (${bucket.key})`,
          key: bucket.key,
          name: bucket.name,
        }))
      } catch (error) {
        // console.error('Error fetching available buckets', error)
      }
    },
    async fetchDispatchReportNoPagination() {
      try {
        const res = await sortingRepository.getDispatchReport({
          date: this.sortingDate,
          bucket_id: Number(this.bucket),
        })
        return res.data.data || []
      } catch (e) {
        if (this.convertAndNotifyError) {
          this.convertAndNotifyError(e)
        }
        return []
      }
    },
    exportPDF() {
      // eslint-disable-next-line new-cap
      const doc = new jsPDF()
      doc.setFontSize(14)
      doc.text('Dispatch Report', 14, 15)
      doc.setFontSize(10)
      doc.text(`Date: ${this.sortingDate}`, 14, 22)
      doc.text(`Bucket: ${this.bucketName || ''}`, 14, 28)

      const columns = this.dispatchReportFields.map(f => f.label)
      const rows = this.filteredDispatchReport.map(item => this.dispatchReportFields.map(f => item[f.key]))
      doc.autoTable({
        head: [columns],
        body: rows,
        startY: 32,
        styles: { fontSize: 9 },
        margin: { top: 20 },
      })
      doc.save(`DispatchReport_${this.sortingDate}.pdf`)
    },
    printTable() {
      const printContents = this.$refs.dispatchTable.$el.cloneNode(true)
      const html = `
        <html>
          <head>
            <title>Print Dispatch Report</title>
            <link rel="stylesheet" href="https://maxcdn.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css">
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.1/css/all.min.css">
          </head>
          <body>
            <h1 class="text-center">Dispatch Report</h1>
            <p class="text-center">Date: ${this.sortingDate}</p>
            <p class="text-center">Bucket: ${this.bucketName || this.bucket}</p>
            ${printContents.outerHTML}
          </body>
        </html>
      `
      const printWindow = window.open('', '', 'width=900,height=600')
      printWindow.document.open()
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.print()
    },
    filterDispatchReport() {
      if (!this.selectedBranch) {
        this.filteredDispatchReport = this.dispatchReport
      } else {
        this.filteredDispatchReport = this.dispatchReport.filter(item => item.branch === this.selectedBranch)
      }
    },
  },
}
</script>
<style scoped>
.close-bucket {
  background-color: #fff !important;
  border: #fff !important;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  color: rgb(186, 0, 0) !important;
  font-weight: 500 !important;
}
.dispatch-button {
  background-color: #fff !important;
  border: #fff !important;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  color: rgb(28, 0, 152) !important;
  font-weight: 500 !important;
}
</style>
